/**
 * The base editor (docs/ux/templates.md): the sections in order with their
 * words, Move up and Move down as the reorder path, add from the vocabulary,
 * remove (core sections ask first), rename, guidance and standing text in
 * dialogs; the metadata rows, the schedule mapping and the question
 * options beneath; the versions on the right with Make live. Saving writes
 * the next version with a note. The phone reads only.
 */

import { reportTypeLabel, type ReportTypeKind } from '@beam/core/status';
import { ChevronLeftIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useBlocker, useParams } from 'react-router';
import { toast } from 'sonner';
import { useOnline } from '@/app/online';
import type { MetadataSource, TemplateSection, TypeBase } from '@/app/types';
import { useIsPhone } from '@/app/viewport';
import { PageHeader } from '@/components/shell/PageHeader';
import { useShellWidth } from '@/components/shell/context';
import { ErrorPanel } from '@/components/shell/states';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { dateTime } from '@/lib/format';
import { KIND_WORD, METADATA_SOURCE_WORD, TIER_WORD } from './describe';
import { problemsOf, useBase, useSaveBase, useSetLive, useVocabulary } from './useTemplates';

type EditDialog = { kind: 'rename' | 'guidance' | 'standing'; key: string } | null;

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

function move(sections: TemplateSection[], key: string, by: -1 | 1): TemplateSection[] {
  const i = sections.findIndex((s) => s.key === key);
  const j = i + by;
  if (i < 0 || j < 0 || j >= sections.length) return sections;
  // A schedule category never leaves its heading; the heading moves with its categories.
  const out = [...sections];
  const [s] = out.splice(i, 1);
  out.splice(j, 0, s!);
  return out;
}

export function BaseEditorPage() {
  useShellWidth('narrow');
  const phone = useIsPhone();
  const online = useOnline();
  const { type = 'ime' } = useParams();
  const reportType = type as ReportTypeKind;
  const base = useBase(reportType);
  const vocabulary = useVocabulary();
  const save = useSaveBase(reportType);
  const setLive = useSetLive();
  const [draft, setDraft] = useState<TypeBase | null>(null);
  const [changeNote, setChangeNote] = useState('');
  const [problems, setProblems] = useState<string[]>([]);
  const [dialog, setDialog] = useState<EditDialog>(null);
  const [dialogValue, setDialogValue] = useState('');
  const [removing, setRemoving] = useState<TemplateSection | null>(null);
  const [makeLive, setMakeLive] = useState<number | null>(null);
  const [addKey, setAddKey] = useState('');
  const problemsRef = useRef<HTMLDivElement>(null);

  const saved = base.data?.version.body ?? null;
  useEffect(() => { if (saved) setDraft(saved); }, [saved]);
  useEffect(() => { if (problems.length > 0) problemsRef.current?.focus(); }, [problems]);
  const dirty = draft !== null && saved !== null && !same(draft, saved);
  // Leaving the route with unsaved work asks first (the doctor page's pattern).
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  const blocker = useBlocker(() => dirtyRef.current);
  const available = useMemo(() => (vocabulary.data?.sections ?? []).filter((v) => !draft?.sections.some((s) => s.key === v.key)), [vocabulary.data, draft]);

  const title = reportTypeLabel(reportType, 'label');
  const back = <Link to="/registry/templates" className="inline-flex items-center gap-1 text-sm text-beam-text-muted hover:text-foreground"><ChevronLeftIcon aria-hidden="true" className="size-4" />Templates</Link>;

  if (base.isError) return <div className="flex flex-col gap-3">{back}<PageHeader title={title} /><ErrorPanel message={`The ${title} base could not be loaded.`} onRetry={() => void base.refetch()} /></div>;
  if (base.isPending || !draft || !base.data) return <div className="flex flex-col gap-3">{back}<PageHeader title={title} /><Skeleton className="h-96 rounded-xl" aria-label="Loading the base" /></div>;
  const view = base.data;
  const nextVersion = view.head.versions + 1;

  function update(patch: Partial<TypeBase>) { setDraft((d) => (d ? { ...d, ...patch } : d)); }
  function updateSection(key: string, patch: Partial<TemplateSection>) { update({ sections: draft!.sections.map((s) => (s.key === key ? { ...s, ...patch } : s)) }); }
  function openDialog(kind: NonNullable<EditDialog>['kind'], s: TemplateSection) {
    setDialogValue(kind === 'rename' ? s.label : kind === 'guidance' ? s.guidance ?? '' : s.boilerplate ?? '');
    setDialog({ kind, key: s.key });
  }
  function applyDialog() {
    if (!dialog) return;
    const value = dialogValue.trim();
    if (dialog.kind === 'rename') { if (!value) return; updateSection(dialog.key, { label: value }); }
    else if (dialog.kind === 'guidance') updateSection(dialog.key, value ? { guidance: value } : { guidance: undefined });
    else updateSection(dialog.key, value ? { boilerplate: value } : { boilerplate: undefined });
    setDialog(null);
  }
  function remove(s: TemplateSection) {
    update({ sections: draft!.sections.filter((x) => x.key !== s.key && x.parent !== s.key) });
    setRemoving(null);
  }
  function add() {
    const v = vocabulary.data?.sections.find((x) => x.key === addKey);
    if (!v) return;
    const q = draft!.sections.findIndex((s) => s.kind === 'questions');
    const section: TemplateSection = { key: v.key, label: v.label, kind: v.kind, tier: 'optional', ...(v.guidance ? { guidance: v.guidance } : {}), ...(v.allowsNotes ? { allowsNotes: true } : {}) };
    const sections = [...draft!.sections];
    sections.splice(q >= 0 ? q : sections.length, 0, section);
    update({ sections });
    setAddKey('');
  }
  async function onSave() {
    setProblems([]);
    try {
      const r = await save.mutateAsync({ body: draft!, changeNote: changeNote.trim() || null });
      toast.success(`Version ${r.version.version} saved and live.`);
      setChangeNote('');
    } catch (err) {
      setProblems(problemsOf(err).problems);
    }
  }
  async function onMakeLive(version: number) {
    setMakeLive(null);
    try {
      await setLive.mutateAsync({ kind: 'base', id: reportType, version });
      toast.success(`Version ${version} is live again.`);
    } catch (err) {
      setProblems(problemsOf(err).problems);
    }
  }

  const sectionRows = draft.sections.map((s, i) => (
    <li key={s.key} data-slot="section-row" data-key={s.key} className={`flex items-center gap-3 border-b border-beam-line py-2 text-sm last:border-b-0 ${s.parent ? 'pl-8' : ''}`}>
      <span className="tabular w-6 shrink-0 text-right text-beam-text-muted">{i + 1}</span>
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="font-medium">{s.label}</span>
        <span className="text-xs text-beam-text-muted">{KIND_WORD[s.kind]}, {TIER_WORD[s.tier]}{s.guidance ? ', guidance set' : ''}{s.boilerplate ? ', standing text set' : ''}</span>
      </span>
      {!phone ? (
        <>
          <Button type="button" variant="ghost" size="sm" className="h-8" aria-label={`Move ${s.label} up`} disabled={i === 0 || !online} onClick={() => update({ sections: move(draft.sections, s.key, -1) })}>Up</Button>
          <Button type="button" variant="ghost" size="sm" className="h-8" aria-label={`Move ${s.label} down`} disabled={i === draft.sections.length - 1 || !online} onClick={() => update({ sections: move(draft.sections, s.key, 1) })}>Down</Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button type="button" variant="outline" size="sm" className="h-8" aria-label={`More for ${s.label}`} disabled={!online}>...</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => openDialog('rename', s)}>Rename</DropdownMenuItem>
              <DropdownMenuItem onSelect={() => openDialog('guidance', s)}>Edit guidance</DropdownMenuItem>
              {s.kind === 'boilerplate' || s.kind === 'prose' ? <DropdownMenuItem onSelect={() => openDialog('standing', s)}>Edit standing text</DropdownMenuItem> : null}
              {s.kind !== 'questions' ? <DropdownMenuItem className="text-beam-error" onSelect={() => (s.tier === 'core' ? setRemoving(s) : remove(s))}>Remove</DropdownMenuItem> : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : null}
    </li>
  ));

  return (
    <div className="flex flex-col gap-4">
      {back}
      <PageHeader
        title={title}
        description={draft.note ?? undefined}
        meta={<span className="text-sm text-beam-text-muted" data-slot="base-version">Base version {view.head.liveVersion} (live){view.head.versions > view.head.liveVersion ? `, ${view.head.versions} versions` : ''}</span>}
        actions={phone ? undefined : (
          <Button type="button" data-primary={dirty ? 'true' : undefined} disabled={!dirty || !online || save.isPending} aria-disabled={!dirty ? 'true' : undefined} title={!dirty ? 'Nothing has changed.' : undefined} onClick={() => void onSave()}>
            {save.isPending ? 'Saving' : `Save as version ${nextVersion}`}
          </Button>
        )}
      />
      {phone ? <p className="text-sm text-beam-text-muted">Edit templates from a desktop.</p> : null}
      <AlertDialog open={blocker.state === 'blocked'} onOpenChange={(o) => { if (!o && blocker.state === 'blocked') blocker.reset(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>The unsaved changes to the {title} base are dropped; version {view.head.liveVersion} stays live.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (blocker.state === 'blocked') blocker.proceed(); }}>Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {problems.length > 0 ? (
        <Alert ref={problemsRef} tabIndex={-1} role="alert" variant="destructive" data-slot="template-problems">
          <AlertTitle>Not saved</AlertTitle>
          <AlertDescription><ul className="list-disc pl-4">{problems.map((p) => <li key={p}>{p}</li>)}</ul></AlertDescription>
        </Alert>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
        <section aria-label="Sections" className="rounded-xl border border-beam-line bg-beam-paper p-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="eyebrow text-beam-text-muted">Sections</h2>
            {!phone ? (
              <div className="flex items-center gap-2">
                <Select value={addKey} onValueChange={setAddKey}>
                  <SelectTrigger aria-label="Section to add" className="h-8 min-w-56 text-[13px]"><SelectValue placeholder="Add a section" /></SelectTrigger>
                  <SelectContent>{available.map((v) => <SelectItem key={v.key} value={v.key}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" className="h-8" disabled={!addKey || !online} onClick={add}>Add</Button>
              </div>
            ) : null}
          </div>
          <ol className="flex flex-col" data-slot="section-list">{sectionRows}</ol>
        </section>
        <aside className="flex flex-col gap-4">
          <section aria-label="Versions" className="rounded-xl border border-beam-line bg-beam-paper p-4">
            <h2 className="eyebrow mb-2 text-beam-text-muted">Versions</h2>
            <ul className="flex flex-col divide-y divide-beam-line text-[13px]" data-slot="version-list">
              {[...view.versions].reverse().map((v) => (
                <li key={v.version} data-version={v.version} className="flex flex-col gap-0.5 py-2">
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-medium tabular">Version {v.version}{v.version === view.head.liveVersion ? <span className="ml-2 rounded-full bg-beam-cream-deep px-2 py-0.5 text-xs font-normal" data-slot="current">current</span> : null}</span>
                    {v.version !== view.head.liveVersion && !phone ? <Button type="button" variant="link" className="h-auto p-0 text-[13px]" disabled={!online} onClick={() => setMakeLive(v.version)}>Make live</Button> : null}
                  </span>
                  <span className="text-beam-text-muted">{dateTime(v.createdAt)}{v.createdByName ? `, ${v.createdByName}` : ''}</span>
                  {v.changeNote ? <span>{v.changeNote}</span> : null}
                </li>
              ))}
            </ul>
          </section>
          {!phone ? (
            <section className="flex flex-col gap-2 rounded-xl border border-beam-line bg-beam-paper p-4">
              <Label htmlFor="change-note" className="eyebrow text-beam-text-muted">Change note</Label>
              <Textarea id="change-note" rows={3} maxLength={400} value={changeNote} placeholder="Say what changed and why" onChange={(e) => setChangeNote(e.target.value)} />
            </section>
          ) : null}
        </aside>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <section aria-label="Metadata rows" className="rounded-xl border border-beam-line bg-beam-paper p-4">
          <h2 className="eyebrow mb-2 text-beam-text-muted">Metadata rows</h2>
          <ul className="flex flex-col divide-y divide-beam-line text-[13px]" data-slot="metadata-list">
            {draft.metadata.map((m) => (
              <li key={m.key} className="flex items-center justify-between gap-2 py-1.5">
                <span className="font-medium">{m.label}</span>
                <span className="text-beam-text-muted">{METADATA_SOURCE_WORD[m.source as MetadataSource]}{m.loiField ? ` (${m.loiField})` : ''}</span>
              </li>
            ))}
          </ul>
        </section>
        <section aria-label="Schedule" className="rounded-xl border border-beam-line bg-beam-paper p-4">
          <h2 className="eyebrow mb-2 text-beam-text-muted">Schedule</h2>
          <p className="mb-2 text-[13px] text-beam-text-muted">{draft.schedule.source === 'casefile' ? 'From the sorted casefile.' : "From the referrer's own list."}</p>
          <ul className="flex flex-col divide-y divide-beam-line text-[13px]">
            {draft.schedule.categories.map((c) => <li key={c.key} className="flex items-center justify-between gap-2 py-1.5"><span className="font-medium">{c.label}</span><span className="text-right text-beam-text-muted">{c.casefileSections.join(', ')}</span></li>)}
          </ul>
        </section>
        <section aria-label="Questions" className="rounded-xl border border-beam-line bg-beam-paper p-4">
          <h2 className="eyebrow mb-2 text-beam-text-muted">Questions</h2>
          <div className="flex flex-col gap-3 text-sm">
            <label className="flex items-start gap-3"><Checkbox checked={draft.questions.restateAsHeading} disabled={phone || !online} onCheckedChange={(v) => update({ questions: { ...draft.questions, restateAsHeading: v === true } })} /><span>Restate the letter's question as the answer's heading</span></label>
            <label className="flex items-start gap-3"><Checkbox checked={draft.questions.subHeadings} disabled={phone || !online} onCheckedChange={(v) => update({ questions: { ...draft.questions, subHeadings: v === true } })} /><span>The doctor's sub-headings under an answer</span></label>
            {reportType === 'supplementary' ? (
              <label className="flex items-start gap-3"><Checkbox checked={draft.supplementaryMetadata === 'restate'} disabled={phone || !online} onCheckedChange={(v) => update({ supplementaryMetadata: v === true ? 'restate' : 'reference' })} /><span>Restate the original report's metadata block (else reference the original report)</span></label>
            ) : null}
          </div>
        </section>
      </div>

      <Dialog open={dialog !== null} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>{dialog?.kind === 'rename' ? 'Rename section' : dialog?.kind === 'guidance' ? 'Edit guidance' : 'Edit standing text'}</DialogTitle>
            <DialogDescription>{dialog?.kind === 'rename' ? 'The title the report shows; the key stays the same.' : dialog?.kind === 'guidance' ? 'Shown to the author above the box.' : 'Text the author does not edit, dropped into the section.'}</DialogDescription>
          </DialogHeader>
          <form className="flex flex-col gap-3" onSubmit={(e) => { e.preventDefault(); applyDialog(); }}>
            {dialog?.kind === 'rename' ? <Input aria-label="Section title" value={dialogValue} maxLength={80} autoFocus onChange={(e) => setDialogValue(e.target.value)} /> : <Textarea aria-label={dialog?.kind === 'guidance' ? 'Guidance' : 'Standing text'} rows={dialog?.kind === 'guidance' ? 4 : 8} maxLength={dialog?.kind === 'guidance' ? 600 : 2000} value={dialogValue} autoFocus onChange={(e) => setDialogValue(e.target.value)} />}
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button type="submit" disabled={dialog?.kind === 'rename' && !dialogValue.trim()}>Apply</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={removing !== null} onOpenChange={(o) => { if (!o) setRemoving(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove {removing?.label} from the base?</AlertDialogTitle><AlertDialogDescription>Removing a core section changes every doctor's {title} report. The change takes effect when you save the next version.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Keep it</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={() => removing && remove(removing)}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={makeLive !== null} onOpenChange={(o) => { if (!o) setMakeLive(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Make version {makeLive} live?</AlertDialogTitle><AlertDialogDescription>Version {view.head.liveVersion} stays in the list and can be made live again.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>Keep version {view.head.liveVersion}</AlertDialogCancel>
            <AlertDialogAction onClick={() => makeLive !== null && void onMakeLive(makeLive)}>Make live</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
